/**
 * Images are resized by the media CDN, never by this server.
 *
 * The Next image optimizer (/_next/image) is not used: it re-encoded every
 * image on the website's own tasks, with a cold cache after each deploy. The
 * media CloudFront distribution runs muvi-media-optimizer instead, which serves
 * `<CMS image URL>?w=<width>` resized to WebP and cached at the edge, so the
 * browser fetches every image straight from the media CDN.
 *
 * Only hosts listed in MEDIA_RESIZE_HOSTS get the `?w=` param; every other image
 * (other hosts, /public assets) is requested exactly as given.
 */
import type { ImageLoader } from 'next/legacy/image';

/**
 * Media hosts whose CloudFront distribution runs the resizer: UAT
 * (d3ui39dhvshx4d) and production (d3th1nqbpcyfdw). A host that does not run it
 * ignores `?w=` and returns the original, so listing one early is heavier but
 * still correct.
 */
const MEDIA_RESIZE_HOSTS = [
  'd3ui39dhvshx4d.cloudfront.net',
  'd3th1nqbpcyfdw.cloudfront.net',
];

/**
 * The widths the resizer accepts. Anything else is rounded up to the next one,
 * and anything above 1920 becomes 1920; the resizer never enlarges, so a 1600 px
 * poster asked for at 1920 comes back at 1600. Mirrored by `images.deviceSizes`
 * in next.config.js, so next/legacy/image builds its srcset from these.
 */
export const MEDIA_WIDTHS = [480, 960, 1920];

export const supportsMediaResize = (src: unknown): src is string => {
  if (typeof src !== 'string') return false;
  try {
    return MEDIA_RESIZE_HOSTS.includes(new URL(src).host);
  } catch {
    // Relative paths (/public assets) have no host.
    return false;
  }
};

const snapToMediaWidth = (width: number): number =>
  MEDIA_WIDTHS.find(w => w >= width) ?? MEDIA_WIDTHS[MEDIA_WIDTHS.length - 1];

/**
 * `src` at `width` from the media CDN, or `src` unchanged when its host does
 * not resize. For images built by hand: CSS backgrounds and preloads.
 */
export const mediaImageUrl = (src: string, width: number): string => {
  if (!supportsMediaResize(src)) return src;
  const url = new URL(src);
  url.searchParams.set('w', String(snapToMediaWidth(width)));
  return url.toString();
};

/**
 * Loader for next/legacy/image, which ignores `images.loaderFile`, so every
 * <Image> passes it as a prop. Pair it with `unoptimized` for sources that do
 * not resize, so those render as a plain `src` without a srcset.
 */
export const mediaImageLoader: ImageLoader = ({ src, width }) =>
  mediaImageUrl(src, width);
